import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBankAccount,
  deleteBankAccount,
  listBankAccounts,
  updateBankAccount,
  type BankAccount,
  type BankAccountInput,
  type BankAccountUpdateInput,
  type DeleteBankAccountResult,
} from '../lib/bankAccountsApi';

// React Query hooks for bank accounts.

export const BANK_ACCOUNTS_KEY = ['bank-accounts'] as const;

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEY });
  // Manual balance edits feed the cashflow "money in accounts" card.
  void qc.invalidateQueries({ queryKey: ['financials', 'cashflow'] });
}

export function useBankAccounts(companyId?: string) {
  return useQuery({
    queryKey: [...BANK_ACCOUNTS_KEY, companyId ?? 'all'],
    queryFn: () => listBankAccounts(companyId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation<BankAccount, Error, BankAccountInput>({
    mutationFn: createBankAccount,
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation<BankAccount, Error, { id: string; input: BankAccountUpdateInput }>({
    mutationFn: ({ id, input }) => updateBankAccount(id, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation<DeleteBankAccountResult, Error, string>({
    mutationFn: deleteBankAccount,
    onSuccess: () => invalidate(qc),
  });
}
